import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { shouldGoMobile } from "../utils/mobileDetect";
import { Box, Typography, Chip, Button, CircularProgress, TextField } from "@mui/material";
import { MainPage } from "../MainPage";
import { useHighlightsAuth } from "./useHighlightsAuth";

// ─── Step definitions ─────────────────────────────────────────────────────────
// Edit `insight` for the chip that shows on each active card.
// Edit `appState` comments to describe what the dashboard should show.

// ─── EDIT THIS to change what the map shows on each step ─────────────────────
// mapView fields:
//   variable        - key from groupsById (e.g. "vote_intention", "trust_energy")
//   compareWaves    - true = show Q1→Q2 change overlay; false = show latest wave only
//   compareKey      - "__top__" = biggest gainers, "__top_neg__" = biggest losers
//   waveSelect      - wave number to display (4 = Q2 2026)
//   preferenceFilter - override sub-key shown (e.g. "pol:nat_issues:defence:cat")
//   highlightAreas  - array of PCON24CD codes to spotlight on the map
// flyTo fields:
//   flyTo        - { type: "fitBounds", bounds } or { center: [lng,lat], zoom }
//   flyTo2       - optional second flyTo fired after flyTo2Delay ms (for pan sequences)
//   flyTo2Delay  - ms before flyTo2 fires (default 2900)
// ─────────────────────────────────────────────────────────────────────────────

// Single source of truth for UK map position — used by all full-UK steps.
// fitBounds with padding adapts to any panel / viewport size automatically.
const UK_BOUNDS = [[-6.5, 49.9], [2.0, 59.0]];
const UK_FIT = (duration = 1200, extraPadding) => ({
  type: "fitBounds",
  bounds: UK_BOUNDS,
  duration,
  padding: extraPadding ?? 30,
});

const STEPS = [
  {
    name: "intro",
    headline: "One national survey. Local estimates for every constituency.",
    copy: "Stack surveyed more than 10,000 British adults online in Q2 2026. The sample was balanced to be nationally representative by age, gender, ethnicity, education, party identification, and region.",
    insight: "10K interviews · 632 constituencies",
    mapLabel: "Vote intention · Q2 2026",
    mapView: { variable: "vote_intention", compareWaves: false, waveSelect: 4 },
    flyTo: UK_FIT(0),  // instant on first load
  },
  {
    name: "tourist-tax-support",
    headline: "Not a single constituency in the country supported it.",
    copy: "We demonstrated that the new tax was deeply unpopular and that every constituency in the country opposed it.\n\nThis headline finding gave the client a clear, national message to build a campaign around.",
    insight: "No constituency net supportive",
    mapLabel: "Support for tax · net score by constituency",
    mapView: { variable: "holiday_tax_support", compareWaves: false, waveSelect: 4 },
    flyTo: UK_FIT(1200),  // smooth pan, same view
  },
  {
    name: "mp-targeting",
    headline: "That opposition translates directly into electoral risk for MPs.",
    copy: "We showed that there would be an electoral cost to MPs who backed it.\n\n40% of Labour's 2024 voters said they would be less likely to vote for their MP again if they supported the tax.\n\nWe could show how this would impact on each MP's majority across the country, with even a number of senior Ministers under threat…",
    insight: "200+ Labour seats at risk",
    mapLabel: "Impact of tax on voting · all voters",
    mapView: { variable: "holiday_tax_vote", compareWaves: false, waveSelect: 4 },
    // Right padding pushes UK left, making room for chart overlay — scales with panel size
    flyTo: UK_FIT(800, { top: 20, bottom: 20, left: 20, right: 120 }),
  },
  {
    name: "local-messaging",
    headline: "Concerns about the tax vary across the country.",
    copy: "We were able to show how different campaign messages would work in different constituencies and regions.\n\nThis allows clients to tailor and target their campaign communications more effectively.",
    insight: "Distinct talking point per MP",
    mapLabel: "Concerns around a tax · top concern by constituency",
    mapView: { variable: "tourist_tax_concerns", compareWaves: false, waveSelect: 4 },
    // Zoom into Norfolk coast, then pull back to full UK after 3.5s
    flyTo: { center: [1.0, 52.8], zoom: 8.5, duration: 1400 },
    flyTo2: UK_FIT(1200),
    flyTo2Delay: 3500,
  },
  {
    name: "reports",
    headline: "Instant briefing packs for any constituency.",
    copy: "Constituency Profiles also allows you to create curated reports with a click of a button.\n\nThis enables you to create a briefing pack for your leadership within seconds ahead of a series of political meetings.",
    insight: "One click · any constituency",
    mapLabel: "Local messaging · top concern by constituency",
    mapView: { variable: "tourist_tax_concerns", compareWaves: false, waveSelect: 4 },
    flyTo: UK_FIT(800),  // full UK behind blur + report image
  },
  {
    name: "cta",
    headline: "Ready to explore?",
    copy: "",
    insight: "Q3 data lands September",
    isCta: true,
    mapLabel: "Vote intention · Q2 2026",
    mapView: { variable: "vote_intention", compareWaves: false },
    flyTo: UK_FIT(),  // full UK behind blur + signup
  },
];

// ─── DemoSignupOverlay ────────────────────────────────────────────────────────
// Shown on the map panel when the user reaches the final CTA slide.

function DemoSignupOverlay({ embedded = false }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/user/demo-signup/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong");
      // In dev Django returns the verify_url directly; in prod it emails the link.
      setSuccess(data?.verify_url ? `Dev link: ${data.verify_url}` : true);
    } catch (err) {
      setError(err.message || "Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
      <Box
        sx={{
          width: embedded ? "100%" : "min(420px, 90%)",
          bgcolor: "#fff",
          borderRadius: embedded ? "0" : "16px",
          p: embedded ? 0 : 3.5,
          boxShadow: embedded ? "none" : "0 24px 60px rgba(0,0,0,0.25)",
          border: "none",
        }}
      >
        {success ? (
          <>
            <Typography sx={{ fontSize: "22px", fontWeight: 800, color: "#0f172a", fontFamily: "'DM Sans', sans-serif", mb: 1 }}>
              Check your email
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#475569", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.6 }}>
              {typeof success === "string"
                ? success
                : "Demo account created. Check your email to verify and activate access."}
            </Typography>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: "22px", fontWeight: 800, color: "#0f172a", fontFamily: "'DM Sans', sans-serif", mb: 0.5 }}>
              Get demo access
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#64748b", fontFamily: "'DM Sans', sans-serif", mb: 2.5, lineHeight: 1.6 }}>
              Explore our tracking metrics for Q2 across all 632 constituencies and demographics.
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <TextField
                size="small"
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: "'DM Sans', sans-serif" } }}
              />
              <TextField
                size="small"
                label="Email"
                type="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: "'DM Sans', sans-serif" } }}
              />
              <TextField
                size="small"
                label="Password"
                type="password"
                required
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: "'DM Sans', sans-serif" } }}
              />
              {error && (
                <Typography sx={{ fontSize: "12px", color: "#dc2626", fontFamily: "'DM Sans', sans-serif" }}>
                  {error}
                </Typography>
              )}
              <Button
                type="submit"
                variant="contained"
                disableElevation
                disabled={submitting || !email || !password}
                sx={{
                  bgcolor: "#0d9488", color: "#fff", fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 700, fontSize: "14px", textTransform: "none",
                  borderRadius: "10px", py: 1.1,
                  boxShadow: "0 4px 20px rgba(13,148,136,0.28)",
                  "&:hover": { bgcolor: "#0f766e" },
                  "&:disabled": { bgcolor: "rgba(13,148,136,0.35)", color: "#fff" },
                }}
              >
                {submitting ? "Creating account…" : "Get demo access →"}
              </Button>
            </Box>
          </>
        )}
      </Box>
  );

  if (embedded) {
    return content;
  }

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(15,23,42,0.55)",
        backdropFilter: "blur(6px)",
        animation: "fadeInOverlay 0.5s ease both",
        "@keyframes fadeInOverlay": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      {content}
    </Box>
  );
}


// ─── AppPanelWrapper ──────────────────────────────────────────────────────────
// Mounts MainPage directly (not as a route) inside the sticky right panel.
// Uses a no-op navigate so internal URL transitions don't leave /highlights/q2-2026.
// Forwards the ref so TaxCaseStudyPage can call applyTheme / setState on the
// MainPage class instance directly from scroll event handlers.

const AppPanelWrapper = React.forwardRef(function AppPanelWrapper(_props, ref) {
  // No-op navigate: prevents any MainPage-internal setThemeInUrl call from
  // routing the browser away from the highlights page.
  const noopNavigate = useCallback(() => {}, []);

  return (
    <MainPage
      ref={ref}
      slug="explore"
      navigate={noopNavigate}
      hideChrome
      skipAuthRedirect
    />
  );
});

// ─── Navigation dispatcher ────────────────────────────────────────────────────
// This is the only place you need to edit when wiring in real app state.
// Each case maps a step name → a direct call on the MainPage class instance.

// ─── Navigation dispatcher ────────────────────────────────────────────────────
// Reads mapView / flyTo config from the STEPS array above.
// You should not need to edit this function - edit the STEPS array instead.

// Holds the pending flyTo2 timeout so we can cancel it on step change
let _flyTo2Timer = null;

function navigateToStep(app, stepName) {
  if (!app) return;
  const step = STEPS.find((s) => s.name === stepName);
  if (!step) return;

  // Cancel any in-flight flyTo2 from a previous step
  if (_flyTo2Timer !== null) {
    clearTimeout(_flyTo2Timer);
    _flyTo2Timer = null;
  }

  // Always clear highlights at the start of each step
  // partyFilter: numeric party key (2 = Labour) or null to clear
  app.setState({ chatHighlightAreas: [], party: step.partyFilter ?? null });

  // Apply map variable / wave / comparison state in a single safe call
  if (step.mapView) {
    app.setHighlightsState(step.mapView);
  }

  // Apply zoom/pan
  if (step.flyTo) {
    app.flyTo(step.flyTo);
  }
  if (step.flyTo2) {
    const delay = step.flyTo2Delay ?? 2900;
    _flyTo2Timer = setTimeout(() => {
      _flyTo2Timer = null;
      app.flyTo(step.flyTo2);
      app.setState({ chatHighlightAreas: step.flyTo2Highlights ?? [] });
    }, delay);
  }
}

// ─── Shared style tokens ──────────────────────────────────────────────────────
const F = "'Inter', 'Roboto', sans-serif";
const TEAL = "#0d9488";
const INK = "#0f172a";
const MUTED = "#475569";
const MUTED_LIGHT = "#94a3b8";
const PAPER = "#ffffff";
const PAPER_SOFT = "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)";
const BORDER = "rgba(15,23,42,0.08)";

// ─── Sub-components ───────────────────────────────────────────────────────────

function StepHeader({ index, total, chapter, isActive, onScrollNext, hasNext }) {
  return (
    <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1, mt: "auto", pt: 2.5, opacity: isActive ? 1 : 0, transition: "opacity 0.35s ease", minHeight: 28 }}>
      <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: TEAL, fontFamily: F }}>
        {index + 1} of {total}
      </Typography>
      {chapter && (
        <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F, letterSpacing: "0.02em" }}>
          · {chapter}
        </Typography>
      )}
      {hasNext && (
        <Box
          onClick={onScrollNext}
          sx={{
            ml: "auto", display: "inline-flex", alignItems: "center", gap: 0.75,
            cursor: "pointer", px: 1.4, py: 0.75, borderRadius: "10px",
            border: `1px solid ${TEAL}30`, bgcolor: `${TEAL}08`,
            transition: "all 0.18s ease",
            "&:hover": { bgcolor: `${TEAL}14`, borderColor: `${TEAL}55` },
          }}
        >
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: TEAL, fontFamily: F, lineHeight: 1 }}>
            Next
          </Typography>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </Box>
      )}
    </Box>
  );
}

function Eyebrow({ children }) {
  return (
    <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#374151", fontFamily: F, mb: 1.6 }}>
      {children}
    </Typography>
  );
}

function Headline({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "18px", sm: "22px", md: "26px" }, fontWeight: 800, lineHeight: { xs: 1.2, md: 1.15 }, letterSpacing: "-0.025em", color: INK, fontFamily: F, mb: 2, maxWidth: 620 }}>
      {children}
    </Typography>
  );
}

function IntroHeadline({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "18px", sm: "21px", md: "26px" }, fontWeight: 700, lineHeight: { xs: 1.35, md: 1.25 }, letterSpacing: "-0.02em", color: INK, fontFamily: F, mb: 2, maxWidth: 600 }}>
      {children}
    </Typography>
  );
}

function BodyCopy({ children }) {
  return (
    <Typography sx={{ fontSize: "13px", color: MUTED, lineHeight: 1.75, fontFamily: F, maxWidth: 560, whiteSpace: "pre-line", letterSpacing: "-0.01em" }}>
      {children}
    </Typography>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#374151", fontFamily: F, mb: 1.1 }}>
      {children}
    </Typography>
  );
}

function Panel({ children, sx = {} }) {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 590,
        p: { xs: 2, md: 2.5 },
        borderRadius: "22px",
        bgcolor: PAPER,
        backgroundImage: PAPER_SOFT,
        border: `1px solid ${BORDER}`,
        boxShadow: "0 18px 38px rgba(15,23,42,0.06)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function WaveHint({ text }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "flex-start", gap: 1, mt: 2.25, px: 1.6, py: 1.1,
      borderRadius: "14px", bgcolor: TEAL, width: "fit-content", maxWidth: 520,
      boxShadow: "0 4px 14px rgba(13,148,136,0.28)",
      animation: "hintEntrance 0.5s cubic-bezier(0.22,1,0.36,1) 0.6s both, hintPulse 3s ease-in-out 2s 3",
      "@keyframes hintEntrance": {
        from: { opacity: 0, transform: "translateY(8px) scale(0.97)" },
        to:   { opacity: 1, transform: "translateY(0) scale(1)" },
      },
      "@keyframes hintPulse": {
        "0%,100%": { boxShadow: "0 4px 14px rgba(13,148,136,0.28)" },
        "50%":     { boxShadow: "0 6px 24px rgba(13,148,136,0.55)", transform: "scale(1.015)" },
      },
    }}>
      <svg style={{ flexShrink: 0, marginTop: 2 }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
        <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
      </svg>
      <Typography sx={{ fontSize: "12px", color: "#fff", fontFamily: F, fontWeight: 600, letterSpacing: "0.01em", lineHeight: 1.55 }}>
        {text || "Toggle Q1 / Q2 in the top-right of the map"}
      </Typography>
    </Box>
  );
}

function ScrollStartHint({ visible }) {
  if (!visible) return null;
  return (
    <Box
      sx={{
        position: "fixed",
        left: "50%",
        bottom: { xs: 18, md: 28 },
        transform: "translateX(-50%)",
        zIndex: 1300,
        pointerEvents: "none",
        animation: "scrollHintIn 0.35s ease both",
        "@keyframes scrollHintIn": {
          from: { opacity: 0, transform: "translateX(-50%) translateY(10px)" },
          to: { opacity: 1, transform: "translateX(-50%) translateY(0)" },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.3,
          px: { xs: 1.8, md: 2.1 },
          py: { xs: 1.1, md: 1.25 },
          borderRadius: "18px",
          bgcolor: "rgba(15,23,42,0.92)",
          color: "#fff",
          boxShadow: "0 20px 44px rgba(15,23,42,0.28)",
          backdropFilter: "blur(12px)",
          maxWidth: { xs: "calc(100vw - 24px)", md: 420 },
        }}
      >
        <Box
          sx={{
            width: 18,
            height: 28,
            borderRadius: "12px",
            border: "1.5px solid rgba(255,255,255,0.8)",
            position: "relative",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 5,
              left: "50%",
              width: 3,
              height: 6,
              borderRadius: "999px",
              bgcolor: "#fff",
              transform: "translateX(-50%)",
              animation: "scrollHintDot 1.25s ease-in-out infinite",
              "@keyframes scrollHintDot": {
                "0%": { opacity: 0.2, transform: "translateX(-50%) translateY(0)" },
                "40%": { opacity: 1, transform: "translateX(-50%) translateY(6px)" },
                "100%": { opacity: 0, transform: "translateX(-50%) translateY(10px)" },
              },
            }}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: { xs: "13px", md: "14px" }, fontWeight: 800, fontFamily: F, letterSpacing: "0.06em", textTransform: "uppercase", lineHeight: 1.1 }}>
            Scroll Down To Continue
          </Typography>
          <Typography sx={{ fontSize: { xs: "12px", md: "12px" }, fontFamily: F, color: "rgba(255,255,255,0.82)", lineHeight: 1.3, mt: 0.3 }}>
            Use your mouse wheel or trackpad. You can scroll anywhere on the page.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function StackLogo() {
  return (
    <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
      <img src="/static/images/logo.png" alt="Stack" style={{ height: 22, width: "auto", display: "block" }} />
    </Box>
  );
}

// Card 1 - methodology layout
const FU_KF = {
  "@keyframes fuIn": {
    from: { opacity: 0, transform: "translateY(16px)" },
    to:   { opacity: 1, transform: "translateY(0)" },
  },
};
const fu = (delay) => ({
  animation: `fuIn 0.55s cubic-bezier(0.22,1,0.36,1) ${delay}s both`,
  ...FU_KF,
});

const fuStagger = (baseDelay, i) => ({
  animation: `fuIn 0.5s cubic-bezier(0.22,1,0.36,1) ${(baseDelay + i * 0.09).toFixed(2)}s both`,
  ...FU_KF,
});

// Step name → chapter label for StepHeader orientation
const CHAPTER_NAMES = {
  "intro":             "Introduction",
  "energy-importance": "Sector Salience",
  "reports":           "Reporting",
  "energy-trust":      "Trust Collapse",
  "back-to-basics":    "Trust Repair",
  "segment":           "Audience Segments",
};

// Counts up from 0 to a numeric value on mount (handles prefix/suffix like "−22pts", "47%")
function CountUp({ value, isActive, duration = 1000 }) {
  const [display, setDisplay] = useState(value);
  const rafRef = useRef(null);
  useEffect(() => {
    if (!isActive) { setDisplay(value); return; }
    const m = String(value).match(/^([^0-9]*)(\d+\.?\d*)([^0-9]*)$/);
    if (!m) { setDisplay(value); return; }
    const [, prefix, numStr, suffix] = m;
    const target = parseFloat(numStr);
    const hasDecimal = numStr.includes(".");
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      const cur = target * ease;
      setDisplay(`${prefix}${hasDecimal ? cur.toFixed(1) : Math.round(cur)}${suffix}`);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [isActive, value, duration]);
  return <>{display}</>;
}

// "Up next:" teaser shown at the bottom of each active card to pull the reader forward
function NextStepPreview({ nextStep }) {
  if (!nextStep || nextStep.isCta) return null;
  const text = nextStep.headline || "";
  const preview = text.length > 62 ? text.slice(0, 62) + "…" : text;
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1.25, mt: 3, pt: 2,
      borderTop: "1px dashed rgba(15,23,42,0.07)",
      animation: "fuIn 0.4s ease 0.9s both", ...FU_KF,
    }}>
      <Typography sx={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, flexShrink: 0 }}>
        Next
      </Typography>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={MUTED_LIGHT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <polyline points="9 18 15 12 9 6"/>
      </svg>
      <Typography sx={{ fontSize: "12px", color: MUTED_LIGHT, fontFamily: F, lineHeight: 1.35, fontStyle: "italic" }}>
        {preview}
      </Typography>
    </Box>
  );
}

// Desktop-only arrow that bounces toward the map panel, telling the user where to look
function MapArrowHint({ text }) {
  return (
    <Box sx={{
      display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1.5, mt: 2.5,
      animation: "fuIn 0.45s ease 0.8s both", ...FU_KF,
    }}>
      <Box sx={{
        display: "flex", alignItems: "center", gap: 0.85,
        px: 1.4, py: 0.9, borderRadius: "10px",
        bgcolor: "rgba(13,148,136,0.07)", border: "1px solid rgba(13,148,136,0.18)",
      }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
        </svg>
        <Typography sx={{ fontSize: "12px", fontWeight: 600, color: TEAL, fontFamily: F, lineHeight: 1 }}>
          {text || "Explore on the map"}
        </Typography>
      </Box>
      <Box sx={{
        animation: "arrowBounce 1.5s ease-in-out 2s infinite",
        "@keyframes arrowBounce": {
          "0%,100%": { transform: "translateX(0)", opacity: 0.55 },
          "50%":     { transform: "translateX(7px)", opacity: 1 },
        },
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
        </svg>
      </Box>
    </Box>
  );
}

function IntroCard({ isActive, step, nextStep, onScrollNext }) {
  return (
    <>
      <Box sx={fu(0.05)}><StackLogo /></Box>
      <Box sx={fu(0.12)}>
        <Eyebrow>Constituency Profiles</Eyebrow>
        <IntroHeadline>{step.headline}</IntroHeadline>
      </Box>
      <Box sx={fu(0.2)}>
        <Typography sx={{ fontSize: "13px", color: MUTED, lineHeight: 1.65, fontFamily: F }}>
          Constituency Profiles uses MRP modelling to turn a single national survey into local estimates for every Westminster constituency and demographic group.
        </Typography>
      </Box>
      <Box sx={fu(0.3)}>
        {/* The case study */}
        <Box sx={{ mt: 1.5, px: 1.6, py: 1.3, borderRadius: "12px", bgcolor: `${TEAL}08`, border: `1px solid ${TEAL}1a` }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: TEAL, fontFamily: F, mb: 0.5 }}>Case study</Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: INK, fontFamily: F, lineHeight: 1.55 }}>
            In May 2026, we used it to help a trade association campaign against a proposed holiday tax. By combining national polling with constituency-level estimates, we were able to give them the evidence they needed, from the headline national picture down to the arguments that would land best with individual MPs.
          </Typography>
        </Box>
        {/* Methodology */}
        <Box sx={{ mt: 1.25, px: 1.4, py: 1, borderRadius: "10px", border: `1px solid ${BORDER}`, bgcolor: PAPER }}>
          <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, lineHeight: 1.55 }}>
            {step.copy}
          </Typography>
        </Box>
        {/* Scroll CTA */}
        <Box onClick={onScrollNext} sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}>
          <Box sx={{ animation: "nudgeDown2 1.6s ease-in-out infinite", "@keyframes nudgeDown2": { "0%,100%": { transform: "translateY(0)", opacity: 0.5 }, "50%": { transform: "translateY(4px)", opacity: 1 } } }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </Box>
          <Typography sx={{ fontSize: "12px", color: MUTED, fontFamily: F, fontStyle: "italic" }}>
            Scroll or click to find out how this worked in practice
          </Typography>
        </Box>
      </Box>
    </>
  );
}

// Card 2 - tourist tax support
const TAX_SUPPORT_BARS = [
  { label: "Strongly support",       pct: 8,  color: "#2E7D32" },
  { label: "Somewhat support",       pct: 16, color: "#66BB6A" },
  { label: "Neither",                pct: 18, color: "#6B7280" },
  { label: "Somewhat oppose",        pct: 22, color: "#EF5350" },
  { label: "Strongly oppose",        pct: 29, color: "#C62828" },
  { label: "Don't know",             pct: 7,  color: "#B0B8C4" },
];

function TouristTaxSupportCard({ step, isActive, nextStep }) {
  return (
    <>
      <StackLogo />
      <Eyebrow>The National Picture</Eyebrow>
      <Typography sx={{ fontSize: { xs: "21px", sm: "26px", md: "32px" }, fontWeight: 800, lineHeight: { xs: 1.2, md: 1.12 }, letterSpacing: "-0.035em", color: INK, fontFamily: F, mb: 2, width: "100%" }}>
        {step.headline}
      </Typography>
      <Typography sx={{ fontSize: { xs: "14px", md: "15px" }, color: MUTED, lineHeight: 1.82, fontFamily: F, letterSpacing: "-0.01em", width: "100%", mb: 2.5 }}>
        {step.copy}
      </Typography>

      {/* Horizontal bar chart */}
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1, mb: 2.5, ...fu(0.25) }}>
        {TAX_SUPPORT_BARS.map((b) => (
          <Box key={b.label} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: "12px", color: MUTED, fontFamily: F, minWidth: 140, textAlign: "right" }}>{b.label}</Typography>
            <Box sx={{ flex: 1, height: 20, borderRadius: "4px", bgcolor: `${b.color}18`, position: "relative" }}>
              <Box sx={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${b.pct}%`, bgcolor: b.color, borderRadius: "4px", transition: "width 0.6s ease" }} />
            </Box>
            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: INK, fontFamily: F, minWidth: 32 }}>{b.pct}%</Typography>
          </Box>
        ))}
      </Box>

      {/* So what callout */}
      <Box sx={{ width: "100%", px: 2.5, py: 2, borderRadius: "14px", bgcolor: TEAL, boxShadow: `0 4px 20px ${TEAL}40`, mb: 2, ...fu(0.45) }}>
        <Typography sx={{ fontSize: "12px", color: "#fff", fontFamily: F, lineHeight: 1.65, fontWeight: 600 }}>
          This gave the client a clear, nationally consistent argument: the tax is unpopular everywhere. No MP could claim their constituents were on board. A powerful foundation for a lobbying campaign.
        </Typography>
      </Box>

    </>
  );
}

// Card 3 - political impact
const IMPACT_CLUSTERS = [
  { label: "It would make me less likely to vote for them", all: 45.7, lab: 39.5, color: "#C62828" },
  { label: "It would make no difference",                   all: 39.7, lab: 47.1, color: "#6B7280" },
  { label: "It would make me more likely to vote for them", all: 5.2,  lab: 5.5,  color: "#2E7D32" },
  { label: "Don't know",                                    all: 9.4,  lab: 7.9,  color: "#B0B8C4" },
];

const LAB_RED = "#9C27B0";

function PoliticalImpactCard({ step, isActive, nextStep }) {
  return (
    <>
      <StackLogo />
      <Eyebrow>Electoral Risk</Eyebrow>
      <Typography sx={{ fontSize: { xs: "21px", sm: "26px", md: "32px" }, fontWeight: 800, lineHeight: { xs: 1.2, md: 1.12 }, letterSpacing: "-0.035em", color: INK, fontFamily: F, mb: 2, width: "100%" }}>
        {step.headline}
      </Typography>
      <Typography sx={{ fontSize: { xs: "14px", md: "15px" }, color: MUTED, lineHeight: 1.82, fontFamily: F, letterSpacing: "-0.01em", width: "100%", mb: 2.5, whiteSpace: "pre-line" }}>
        {step.copy}
      </Typography>

      {/* Labour callout header + copy */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75, ...fu(0.15) }}>
        <Box sx={{ width: 3, height: 20, borderRadius: "2px", bgcolor: "#C62828", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: INK, fontFamily: F }}>Labour 2024 voters: how would they respond?</Typography>
      </Box>
      <Typography sx={{ fontSize: { xs: "14px", md: "15px" }, color: MUTED, lineHeight: 1.82, fontFamily: F, letterSpacing: "-0.01em", width: "100%", mb: 1.5, ...fu(0.18) }}>
        For Labour, 39.5% of their 2024 voters say they would be less likely to vote for their MP if they supported the tax. This helped the client appeal directly to backbenchers concerned about Labour's ability to win a second term.
      </Typography>

      {/* Legend */}
      <Box sx={{ display: "flex", gap: 2.5, mb: 1.5, ...fu(0.18) }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ width: 22, height: 10, borderRadius: "3px", bgcolor: "rgba(15,23,42,0.15)" }} />
          <Typography sx={{ fontSize: "11px", color: MUTED, fontFamily: F }}>All voters</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ width: 22, height: 14, borderRadius: "3px", bgcolor: "rgba(15,23,42,0.55)" }} />
          <Typography sx={{ fontSize: "11px", color: INK, fontFamily: F, fontWeight: 600 }}>Labour 2024 voters</Typography>
        </Box>
      </Box>

      {/* Clustered bars */}
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.75, mb: 2.5, ...fu(0.25) }}>
        {IMPACT_CLUSTERS.map((c) => (
          <Box key={c.label}>
            <Typography sx={{ fontSize: "11px", color: MUTED, fontFamily: F, mb: 0.5 }}>{c.label}</Typography>
            {/* All voters row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
              <Box sx={{ flex: 1, height: 11, borderRadius: "3px", bgcolor: `${c.color}20`, position: "relative" }}>
                <Box sx={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${c.all}%`, bgcolor: c.color, opacity: 0.4, borderRadius: "3px", transition: "width 0.6s ease" }} />
              </Box>
              <Typography sx={{ fontSize: "11px", color: MUTED, fontFamily: F, minWidth: 28 }}>{c.all}%</Typography>
            </Box>
            {/* Labour row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ flex: 1, height: 15, borderRadius: "3px", bgcolor: `${c.color}20`, position: "relative" }}>
                <Box sx={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${c.lab}%`, bgcolor: c.color, borderRadius: "3px", transition: "width 0.6s ease" }} />
              </Box>
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: INK, fontFamily: F, minWidth: 28 }}>{c.lab}%</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* So what */}
      <Box sx={{ width: "100%", px: 2.5, py: 2, borderRadius: "14px", bgcolor: TEAL, boxShadow: `0 4px 20px ${TEAL}40`, mb: 2, ...fu(0.5) }}>
        <Typography sx={{ fontSize: "12px", color: "#fff", fontFamily: F, lineHeight: 1.65, fontWeight: 600 }}>
          No party wants to be seen voting against their own supporters. Showing Labour MPs that 39.5% of their own 2024 voters would penalise them gave the client a direct line into the backbench conversation.
        </Typography>
      </Box>
    </>
  );
}

// Card 4 - MP targeting table
const MP_ROWS = [
  { constituency: "Pontefract, Castleford and Knottingley", mp: "Yvette Cooper",       gssCode: "E14001428", majority: 6630, labPct: 41.6, labN: 7114, center: [-1.31, 53.69], zoom: 10 },
  { constituency: "Wycombe",                                mp: "Emma Reynolds",        gssCode: "E14001600", majority: 4591, labPct: 39.3, labN: 6310, center: [-0.75, 51.63], zoom: 10 },
  { constituency: "Houghton and Sunderland South",          mp: "Bridget Phillipson",   gssCode: "E14001295", majority: 7169, labPct: 41.9, labN: 7891, center: [-1.41, 54.85], zoom: 10 },
];

function MpTargetingCard({ step, isActive, nextStep, mainRef, zoomedMpCode, onMpZoom }) {
  const activeCode = zoomedMpCode;

  const handleRowClick = (row) => {
    const next = activeCode === row.gssCode ? null : row.gssCode;
    onMpZoom(next);
    if (mainRef?.current) {
      mainRef.current.setState({ chatHighlightAreas: next ? [next] : [], chatShouldZoom: false });
      if (next) {
        mainRef.current.flyTo({ center: row.center, zoom: row.zoom, duration: 1000 });
      } else {
        mainRef.current.flyTo(UK_FIT(1000, { top: 20, bottom: 20, left: 20, right: 120 }));
      }
    }
  };

  return (
    <>
      <StackLogo />
      <Eyebrow>Seat-Level Risk</Eyebrow>
      <Typography sx={{ fontSize: { xs: "21px", sm: "26px", md: "32px" }, fontWeight: 800, lineHeight: { xs: 1.2, md: 1.12 }, letterSpacing: "-0.035em", color: INK, fontFamily: F, mb: 2, width: "100%" }}>
        {step.headline}
      </Typography>
      <Typography sx={{ fontSize: { xs: "14px", md: "15px" }, color: MUTED, lineHeight: 1.82, fontFamily: F, letterSpacing: "-0.01em", width: "100%", mb: 2.5 }}>
        {step.copy}
      </Typography>

      {/* Click hint */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25, ...fu(0.2) }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
        </svg>
        <Typography sx={{ fontSize: "11px", color: MUTED, fontFamily: F, fontStyle: "italic" }}>
          Click an MP below to zoom to their seat on the map
        </Typography>
      </Box>

      {/* Table */}
      <Box sx={{ width: "100%", mb: 2.5, ...fu(0.25) }}>
        {/* Header */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 100px 90px 90px", gap: 1, px: 1.5, pb: 1, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#374151", fontFamily: F }}>Constituency / MP</Typography>
          <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#374151", fontFamily: F, textAlign: "right" }}>Majority</Typography>
          <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#374151", fontFamily: F, textAlign: "right" }}>Lab % less likely</Typography>
          <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#374151", fontFamily: F, textAlign: "right" }}>Lab voters at risk</Typography>
        </Box>

        {MP_ROWS.map((row, i) => {
          const atRisk = row.labN > row.majority;
          const isSelected = activeCode === row.gssCode;
          return (
            <Box
              key={row.mp}
              onClick={() => handleRowClick(row)}
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 100px 90px 90px",
                gap: 1,
                px: 1.5, py: 1.25,
                borderBottom: `1px solid ${BORDER}`,
                bgcolor: isSelected ? `${TEAL}08` : atRisk ? "rgba(198,40,40,0.03)" : "transparent",
                border: isSelected ? `1px solid ${TEAL}30` : "1px solid transparent",
                borderRadius: isSelected ? "8px" : 0,
                cursor: "pointer",
                transition: "background 0.15s ease, border-color 0.15s ease",
                "&:hover": { bgcolor: isSelected ? `${TEAL}10` : "rgba(15,23,42,0.03)" },
                ...fuStagger(0.25, i),
              }}
            >
              {/* Constituency + MP */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                {/* Avatar placeholder */}
                <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: isSelected ? `${TEAL}15` : "rgba(15,23,42,0.08)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s ease" }}>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: isSelected ? TEAL : MUTED, fontFamily: F }}>
                    {row.mp.split(" ").map(w => w[0]).slice(-2).join("")}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: "12px", fontWeight: 700, color: isSelected ? TEAL : INK, fontFamily: F, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.mp}</Typography>
                  <Typography sx={{ fontSize: "11px", color: MUTED, fontFamily: F, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.constituency}</Typography>
                </Box>
              </Box>
              {/* Majority */}
              <Typography sx={{ fontSize: "13px", fontWeight: 600, color: INK, fontFamily: F, textAlign: "right", alignSelf: "center" }}>
                {row.majority.toLocaleString()}
              </Typography>
              {/* Lab % */}
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#C62828", fontFamily: F, textAlign: "right", alignSelf: "center" }}>
                {row.labPct}%
              </Typography>
              {/* Lab N — highlight in red if exceeds majority */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5, alignSelf: "center" }}>
                {atRisk && (
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#C62828", flexShrink: 0 }} />
                )}
                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: atRisk ? "#C62828" : INK, fontFamily: F }}>
                  {row.labN.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          );
        })}

        <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F, mt: 1, px: 1.5 }}>
          Red dot = Labour voters at risk exceed the MP's 2024 majority
        </Typography>
      </Box>
    </>
  );
}

// Card 5 - local messaging
const CONCERN_ITEMS = [
  { label: "Local businesses having to close",    color: "#D32F2F" },
  { label: "Fewer jobs for local young people",   color: "#1976D2" },
  { label: "Less investment in the local area",   color: "#00897B" },
  { label: "Unemployment increase locally",       color: "#F57C00" },
  { label: "General decline locally",             color: "#7B1FA2" },
];

function LocalMessagingCard({ step, isActive, nextStep }) {
  return (
    <>
      <StackLogo />
      <Eyebrow>Local Strategy</Eyebrow>
      <Typography sx={{ fontSize: { xs: "21px", sm: "26px", md: "32px" }, fontWeight: 800, lineHeight: { xs: 1.2, md: 1.12 }, letterSpacing: "-0.035em", color: INK, fontFamily: F, mb: 2, width: "100%" }}>
        {step.headline}
      </Typography>
      <Typography sx={{ fontSize: { xs: "14px", md: "15px" }, color: MUTED, lineHeight: 1.82, fontFamily: F, letterSpacing: "-0.01em", width: "100%", mb: 2.5 }}>
        {step.copy}
      </Typography>

      {/* Concern legend */}
      <Box sx={{ width: "100%", mb: 2.5, ...fu(0.25) }}>
        <SectionLabel>Map legend: top concern by constituency</SectionLabel>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {CONCERN_ITEMS.map((c, i) => (
            <Box key={c.label} sx={{ display: "flex", alignItems: "center", gap: 1.25, ...fuStagger(0.2, i) }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: c.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "13px", color: INK, fontFamily: F, lineHeight: 1.4 }}>{c.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* So what */}
      <Box sx={{ width: "100%", px: 2.5, py: 2, borderRadius: "14px", bgcolor: TEAL, boxShadow: `0 4px 20px ${TEAL}40`, mb: 2, ...fu(0.5) }}>
        <Typography sx={{ fontSize: "12px", color: "#fff", fontFamily: F, lineHeight: 1.65, fontWeight: 600 }}>
          This localised insight gave the client a hook for local and regional press, and distinct talking points for each MP.
        </Typography>
      </Box>

    </>
  );
}

// Card 5 - reports
function ReportsCard({ step, isActive, nextStep }) {
  return (
    <>
      <StackLogo />
      <Eyebrow>Reporting</Eyebrow>
      <Typography sx={{ fontSize: { xs: "21px", sm: "26px", md: "32px" }, fontWeight: 800, lineHeight: { xs: 1.2, md: 1.12 }, letterSpacing: "-0.035em", color: INK, fontFamily: F, mb: 2, width: "100%" }}>
        {step.headline}
      </Typography>
      <Typography sx={{ fontSize: { xs: "14px", md: "15px" }, color: MUTED, lineHeight: 1.82, fontFamily: F, letterSpacing: "-0.01em", width: "100%", mb: 2.5, whiteSpace: "pre-line" }}>
        {step.copy}
      </Typography>
    </>
  );
}

// Card 6 - bullets layout
function BackToBasicsCard({ step, isActive, nextStep }) {
  return (
    <>
      <StackLogo />
      <Eyebrow>Trust Repair</Eyebrow>
      <Headline>{step.headline}</Headline>
      <Panel sx={{ mb: 2.2 }}>
        <SectionLabel>What Voters Want Business To Prioritise</SectionLabel>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.85 }}>
          {step.bullets.map((b, i) => (
            <Box key={b.label} sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.25, py: 1, borderRadius: "12px", bgcolor: b.dir === "up" ? "rgba(16,185,129,0.05)" : "rgba(239,68,68,0.05)", border: `1px solid ${b.dir === "up" ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.12)"}`, ...fuStagger(0.2, i) }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 800, fontFamily: F, color: b.dir === "up" ? "#059669" : "#dc2626", minWidth: 50 }}>
                {b.delta}
              </Typography>
              <Typography sx={{ fontSize: "13px", color: INK, fontFamily: F, flex: 1, lineHeight: 1.35 }}>{b.label}</Typography>
              <Typography sx={{ fontSize: "12px", color: MUTED_LIGHT, fontFamily: F, fontWeight: 700 }}>{b.value}</Typography>
            </Box>
          ))}
        </Box>
      </Panel>
      <BodyCopy>{step.copy}</BodyCopy>
      {step.waveToggleHint && <WaveHint />}
    </>
  );
}

// Card 3 - sector falls + Scotland list
function EnergyImportanceCard({ step, isActive, nextStep }) {
  return (
    <>
      <StackLogo />
      <Eyebrow>{step.name === "scotland-energy" ? "Geographic Signal" : "Sector Salience"}</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      {step.sectorFalls && (
        <Panel sx={{ mt: 2.2, mb: 2.6 }}>
          <SectionLabel>Sectors That Fell Back</SectionLabel>
          {step.sectorFalls.map((s, i) => (
            <Box key={s.label} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", mb: 1, ...fuStagger(0.2, i) }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 800, color: "#dc2626", fontFamily: F, minWidth: 52 }}>{s.delta}</Typography>
              <Box>
                <Typography sx={{ fontSize: "13px", color: INK, fontFamily: F, lineHeight: 1.45 }}>{s.label}{s.from ? ` (${s.from} → ${s.to})` : s.to ? ` → ${s.to}` : ""}</Typography>
                {s.note ? <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, lineHeight: 1.4, mt: 0.15 }}>{s.note}</Typography> : null}
              </Box>
            </Box>
          ))}
        </Panel>
      )}

      {step.mapHoverHint && <MapArrowHint text="Hover over a constituency on the map to explore" />}

      {step.scotlandConstituencies && (
      <Panel sx={{ mt: 2.5 }}>
        <SectionLabel>Scottish Constituencies With The Biggest Gains</SectionLabel>
        {step.scotlandConstituencies.map((c, i) => (
          <Box key={c.name} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75, ...fuStagger(0.15, i) }}>
            <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, minWidth: 14 }}>{i + 1}.</Typography>
            <Typography sx={{ fontSize: "13px", color: INK, fontFamily: F, flex: 1 }}>{c.name}</Typography>
            <Typography sx={{ fontSize: "12px", fontWeight: 800, color: "#059669", fontFamily: F }}>{c.delta}</Typography>
            <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F }}>({c.now})</Typography>
          </Box>
        ))}
        <Typography sx={{ fontSize: "13px", color: MUTED, fontFamily: F, mt: 1.4, lineHeight: 1.72, maxWidth: 520 }}>{step.scotlandNote}</Typography>
      </Panel>
      )}
    </>
  );
}

// Card 5 - trust stats
function EnergyTrustCard({ step, isActive, nextStep }) {
  return (
    <>
      <StackLogo />
      <Eyebrow>Trust Collapse</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      <Panel sx={{ mt: 2.2, mb: 2.2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.85 }}>
          {step.trustStats.map((s, i) => (
            <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.25, py: 1, borderRadius: "12px", bgcolor: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.12)", ...fuStagger(0.2, i) }}>
              {s.highlight ? (
                <>
                  <Typography sx={{ fontSize: "15px", fontWeight: 800, color: "#dc2626", fontFamily: F, minWidth: 52 }}>
                    <CountUp value={s.delta} isActive={isActive} />
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: "13px", color: INK, fontFamily: F, lineHeight: 1.35 }}>{s.label}</Typography>
                    {s.note && <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, mt: 0.15 }}>{s.note}</Typography>}
                  </Box>
                </>
              ) : (
                <>
                  <Typography sx={{ fontSize: "13px", color: MUTED, fontFamily: F, flex: 1 }}>{s.label}</Typography>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: s.value?.startsWith("+") ? "#059669" : "#dc2626", fontFamily: F }}>{s.value}</Typography>
                  <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, mx: 0.5 }}>{s.arrow}</Typography>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#dc2626", fontFamily: F }}>{s.value2}</Typography>
                  <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F }}>{s.label2}</Typography>
                </>
              )}
            </Box>
          ))}
        </Box>
      </Panel>

      {step.waveToggleHint && <WaveHint text="Toggle between Q1 and Q2 in the top-right to see how sharply sentiment shifted in a single quarter - even among the most sympathetic voter groups." />}
    </>
  );
}

// Card 5 - defence demographic stats
// Card 6 - segment cards
function SegmentCard({ step, isActive, nextStep }) {
  return (
    <>
      <StackLogo />
      <Eyebrow>Audience Map</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      <Panel sx={{ mt: 2.2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {step.segments.map((s, i) => (
            <Box key={s.name} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, px: 1.3, py: 1, borderRadius: "14px", bgcolor: "#f8f9fc", border: "1px solid rgba(15,23,42,0.07)", borderLeft: `3px solid ${s.color}`, ...fuStagger(0.15, i) }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.3 }}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: INK, fontFamily: F }}>{s.name}</Typography>
                  <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, fontWeight: 700 }}>· {s.pct}</Typography>
                </Box>
                <Typography sx={{ fontSize: "12px", color: MUTED, fontFamily: F, lineHeight: 1.5 }}>{s.desc}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Panel>
    </>
  );
}

// ─── NarrativeCard - routes to the right sub-component ───────────────────────

function NarrativeCard({ step, index, total, isActive, cardRef, authState, onScrollNext, mainRef, zoomedMpCode, onMpZoom }) {
  const navigate = useNavigate();

  const cardSx = {
    // mobile: height:auto so cards size to content (no internal scroll box)
    // minHeight:60vh gives the IntersectionObserver enough scroll distance per step
    // desktop: fixed viewport height keeps snap-scroll working
    height: { xs: step.isCta ? "calc(100dvh - 47px - 54px)" : "calc(70dvh - 47px - 54px)", md: "calc(100vh - 47px)" },
    minHeight: { xs: "unset", md: "unset" },
    flexShrink: 0,
    scrollSnapAlign: { xs: "none", md: "start" },
    scrollSnapStop: { xs: "normal", md: "always" },
    display: { xs: isActive ? "flex" : "none", md: "flex" },
    flexDirection: "column",
    justifyContent: step.isCta ? "center" : "flex-start",
    alignItems: step.isCta ? "center" : "flex-start",
    pt: { xs: 2.5, sm: "clamp(48px, 12vh, 18vh)", md: "clamp(56px, 15vh, 22vh)" },
    pb: { xs: 8, sm: 3, md: 3 },
    px: { xs: 2.5, sm: 4, md: 6 },
    position: "relative",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch", // mobile: no internal scroll; window is the scroll container
    borderLeft: "1px solid",
    borderColor: isActive ? "rgba(13,148,136,0.2)" : "transparent",
    opacity: isActive ? 1 : 0.4,
    transition: "opacity 0.4s ease, border-color 0.4s ease",
    background: isActive ? "linear-gradient(90deg, rgba(13,148,136,0.04) 0%, rgba(13,148,136,0) 18%)" : "transparent",
  };

  if (step.isCta) {
    return (
      <Box ref={cardRef} data-index={index} sx={cardSx}>
        {isActive ? (
          <Box
            sx={{
              position: { xs: "relative", md: "fixed" },
              inset: { xs: "auto", md: "47px 0 0 0" },
              zIndex: 20,
              height: { xs: "auto", md: "calc(100vh - 47px)" }, /* auto on mobile: card is height:auto so 100% would collapse */
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              px: { xs: 0, md: 6 },
            }}
          >
            <Panel sx={{ maxWidth: 560, textAlign: "center", p: { xs: 3, md: 4 }, pointerEvents: "auto" }}>
              <Eyebrow>Next Step</Eyebrow>
              <Typography sx={{ fontSize: { xs: "30px", md: "38px" }, fontWeight: 800, letterSpacing: "-0.04em", color: INK, fontFamily: F, mb: 1.2, textAlign: "center" }}>
                {step.headline}
              </Typography>

              {authState === "loggedIn" && (
                <>
                  <Typography sx={{ fontSize: "12px", color: MUTED, fontFamily: F, mb: 3.2, textAlign: "center", lineHeight: 1.8 }}>
                    Everything here is available today, filterable by constituency, demographic, and segment.
                  </Typography>
                  <Button variant="contained" disableElevation onClick={() => navigate(shouldGoMobile() ? "/m?section=map" : "/explore")}
                    sx={{ bgcolor: TEAL, color: "#fff", fontFamily: F, fontWeight: 700, fontSize: "14px", textTransform: "none", borderRadius: "999px", px: 3.5, py: 1.25, boxShadow: "0 12px 26px rgba(13,148,136,0.22)", "&:hover": { bgcolor: "#0f766e" } }}>
                    Open the dashboard
                  </Button>
                </>
              )}

              {authState === "loggedOut" && (
                <>
                  <Typography sx={{ fontSize: "12px", color: MUTED, fontFamily: F, mb: 3.2, textAlign: "center", lineHeight: 1.8 }}>
                    Everything here is available today, filterable by constituency, demographic, and segment.
                  </Typography>
                  <Button variant="contained" disableElevation onClick={() => navigate("/")}
                    sx={{ bgcolor: TEAL, color: "#fff", fontFamily: F, fontWeight: 700, fontSize: "14px", textTransform: "none", borderRadius: "999px", px: 3.5, py: 1.25, boxShadow: "0 12px 26px rgba(13,148,136,0.22)", "&:hover": { bgcolor: "#0f766e" } }}>
                    Log in
                  </Button>
                </>
              )}

              {authState === "guest" && (
                <>
                  <Typography sx={{ fontSize: "15px", color: MUTED, fontFamily: F, mb: 2.5, textAlign: "center", lineHeight: 1.8 }}>
                    Everything here is available today, filterable by constituency, demographic, and segment. Sign up now for demo access.
                  </Typography>
                  <DemoSignupOverlay embedded />
                </>
              )}
            </Panel>
          </Box>
        ) : null}
      </Box>
    );
  }

  const nextStep = STEPS[index + 1];

  const inner = (() => {
    switch (step.name) {
      case "intro":                 return <IntroCard isActive={isActive} step={step} nextStep={nextStep} onScrollNext={onScrollNext} />;
      case "tourist-tax-support":   return <TouristTaxSupportCard step={step} isActive={isActive} nextStep={nextStep} />;
      case "political-impact":      return <PoliticalImpactCard step={step} isActive={isActive} nextStep={nextStep} />;
      case "mp-targeting":          return <MpTargetingCard step={step} isActive={isActive} nextStep={nextStep} mainRef={mainRef} zoomedMpCode={zoomedMpCode} onMpZoom={onMpZoom} />;
      case "local-messaging":       return <LocalMessagingCard step={step} isActive={isActive} nextStep={nextStep} />;
      case "back-to-basics":        return <BackToBasicsCard step={step} isActive={isActive} nextStep={nextStep} />;
      case "energy-importance":     return <EnergyImportanceCard step={step} isActive={isActive} nextStep={nextStep} />;
      case "reports":               return <ReportsCard step={step} isActive={isActive} nextStep={nextStep} />;
      case "energy-trust":    return <EnergyTrustCard step={step} isActive={isActive} nextStep={nextStep} />;
      case "segment":         return <SegmentCard step={step} isActive={isActive} nextStep={nextStep} />;
      default:
        return (
          <>
            <Headline>{step.headline}</Headline>
            {step.copy && <BodyCopy>{step.copy}</BodyCopy>}
          </>
        );
    }
  })();

  return (
    <Box ref={cardRef} data-index={index} sx={cardSx}>
      <Box
        key={isActive ? "active" : "idle"}
        sx={{
          display: "flex", flexDirection: "column", flex: 1, width: "100%",
          animation: isActive ? "cardEnter 0.45s cubic-bezier(0.22,1,0.36,1) both" : "none",
          "@keyframes cardEnter": {
            from: { opacity: 0.3, transform: "translateY(10px)" },
            to:   { opacity: 1,   transform: "translateY(0)" },
          },
        }}
      >
        {inner}
        <StepHeader index={index} total={total} isActive={isActive} chapter={CHAPTER_NAMES[step.name]} onScrollNext={onScrollNext} hasNext={!step.isCta && !!STEPS[index + 1]} />
      </Box>
    </Box>
  );
}

// ─── Main page component ──────────────────────────────────────────────────────

export default function TaxCaseStudyPage() {
  const { status: authStatus, authState } = useHighlightsAuth();
  const navigate = useNavigate();

  const [activeIndex, setActiveIndex] = useState(0);
  const [waveOverride, setWaveOverride] = useState(null); // null = use step default
  const [mapFullscreen, setMapFullscreen] = useState(false); // xs only: fullscreen overlay toggle
  const [showStartHint, setShowStartHint] = useState(true);
  const [zoomedMpCode, setZoomedMpCode] = useState(null); // gssCode of the MP seat currently zoomed in
  const cardRefs = useRef([]);        // array of refs, one per narrative card
  const visibleSet = useRef(new Set()); // tracks which card indices are in the active zone
  const mainRef = useRef(null);       // ref to the MainPage class instance
  const scrollContainerRef = useRef(null); // the snap scroll container
  const appPanelRef        = useRef(null); // ref for imperative wheel/touch listeners
  const rightPanelRef      = useRef(null); // ref to the fixed right panel
  const activeIndexRef     = useRef(activeIndex); // always-current index for touch handler closure
  const isFinalStep = STEPS[activeIndex]?.name === "cta";

  // Clear MP zoom when navigating away from the mp-targeting slide
  useEffect(() => {
    if (STEPS[activeIndex]?.name !== "mp-targeting" && zoomedMpCode) {
      setZoomedMpCode(null);
      if (mainRef?.current) {
        mainRef.current.setState({ chatHighlightAreas: [], chatShouldZoom: false });
      }
    }
  }, [activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep activeIndexRef in sync so touch-end closure always reads the latest step
  useEffect(() => { activeIndexRef.current = activeIndex; }, [activeIndex]);

  const scrollToIndex = useCallback((i) => {
    const card = cardRefs.current[i];
    if (!card) return;
    const container = scrollContainerRef.current;
    if (container) container._scrollLocked = true;
    card.scrollIntoView({ behavior: "smooth", block: "start" });
    window.setTimeout(() => { if (container) container._scrollLocked = false; }, 900);
  }, []);

  // Tell Mapbox to redraw at the new container size after a fullscreen toggle.
  useEffect(() => {
    const getMapInstance = () =>
      mainRef.current?.mapRef?.current?.mapRef?.current?.getMap?.();

    getMapInstance()?.resize?.();

    const t = setTimeout(() => {
      const m = getMapInstance();
      if (!m) return;
      m.resize();
      const step = STEPS[activeIndexRef.current];
      if (step?.flyTo) {
        mainRef.current?.flyTo?.(step.flyTo);
      }
    }, 350);

    return () => clearTimeout(t);
  }, [mapFullscreen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWaveToggle = (wave) => {
    if (!mainRef.current) return;
    setWaveOverride(wave);
    const step = STEPS[activeIndex];
    if (step?.mapView) {
      mainRef.current.setHighlightsState({ ...step.mapView, waveSelect: wave });
    }
  };

  // ── Dispatch app navigation when active step changes ────────────────────────
  useEffect(() => {
    if (authStatus !== "ready") return;
    setWaveOverride(null); // reset manual toggle on every step change
    const stepName = STEPS[activeIndex]?.name;
    if (stepName) navigateToStep(mainRef.current, stepName);
  }, [activeIndex, authStatus]);

  useEffect(() => {
    if (activeIndex > 0 && showStartHint) {
      setShowStartHint(false);
    }
  }, [activeIndex, showStartHint]);

  useEffect(() => {
    if (!showStartHint) return;
    const dismiss = () => setShowStartHint(false);
    window.addEventListener("wheel", dismiss, { passive: true, once: true });
    window.addEventListener("touchstart", dismiss, { passive: true, once: true });
    window.addEventListener("keydown", dismiss, { once: true });
    return () => {
      window.removeEventListener("wheel", dismiss);
      window.removeEventListener("touchstart", dismiss);
      window.removeEventListener("keydown", dismiss);
    };
  }, [showStartHint]);

  // ── IntersectionObserver: vanilla JS, no library ─────────────────────────
  // A card becomes "active" when its top edge crosses 40% down from the top
  // of the viewport. We track all currently-visible cards and make the
  // highest-index one active (most recently scrolled into view).
  useEffect(() => {
    if (authStatus !== "ready") return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = Number(entry.target.dataset.index);
          if (entry.isIntersecting) {
            visibleSet.current.add(idx);
          } else {
            visibleSet.current.delete(idx);
          }
        });

        if (visibleSet.current.size > 0) {
          // Highest index = last card to enter the active zone while scrolling down.
          // Falls back to lowest when scrolling back up (last to exit the zone).
          setActiveIndex(Math.max(...visibleSet.current));
        }
      },
      {
        // Desktop: observe within the snap-scroll container.
        // Mobile: container is height:auto and window is the scroll element,
        // so use null (viewport) as the root so the observer fires on window scroll.
        root: window.innerWidth < 900 ? null : scrollContainerRef.current,
        rootMargin: "0px 0px -40% 0px",
        threshold: 0,
      }
    );

    const cards = cardRefs.current;
    cards.forEach((card) => { if (card) observer.observe(card); });

    return () => observer.disconnect();
  }, [authStatus]);

  // ── Wheel (desktop) + touch (mobile) — non-passive, imperative ───────────
  useEffect(() => {
    if (authStatus !== "ready") return;
    const panel     = appPanelRef.current;
    const container = scrollContainerRef.current;
    if (!panel) return;

    const isMobile = () => window.innerWidth < 900;

    const onWheel = (e) => {
      e.preventDefault();
      if (isMobile()) {
        const current = activeIndexRef.current;
        const next = e.deltaY > 0
          ? Math.min(current + 1, STEPS.length - 1)
          : Math.max(current - 1, 0);
        if (next === current) return;
        setActiveIndex(next);
        requestAnimationFrame(() => {
          cardRefs.current[next]?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      } else {
        if (!container || container._scrolling || container._scrollLocked) return;
        container._scrolling = true;
        setTimeout(() => { container._scrolling = false; }, 800);
        container.scrollBy({
          top: e.deltaY > 0 ? container.clientHeight : -container.clientHeight,
          behavior: "smooth",
        });
      }
    };

    let touchStartY = 0;
    const onTouchStart = (e) => { touchStartY = e.touches[0].clientY; };
    const onTouchMove = (e) => { e.preventDefault(); };
    const onTouchEnd = (e) => {
      const delta = touchStartY - e.changedTouches[0].clientY;
      if (Math.abs(delta) < 30) return;
      const current = activeIndexRef.current;
      const next = delta > 0
        ? Math.min(current + 1, STEPS.length - 1)
        : Math.max(current - 1, 0);
      if (next === current) return;
      setActiveIndex(next);
      requestAnimationFrame(() => {
        cardRefs.current[next]?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    };

    panel.addEventListener("wheel",      onWheel,      { passive: false });
    panel.addEventListener("touchstart", onTouchStart, { passive: true  });
    panel.addEventListener("touchmove",  onTouchMove,  { passive: false });
    panel.addEventListener("touchend",   onTouchEnd,   { passive: true  });

    return () => {
      panel.removeEventListener("wheel",      onWheel);
      panel.removeEventListener("touchstart", onTouchStart);
      panel.removeEventListener("touchmove",  onTouchMove);
      panel.removeEventListener("touchend",   onTouchEnd);
    };
  }, [authStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Progress: 0–100 based on active step index ────────────────────────────
  const progress = ((activeIndex) / (STEPS.length - 1)) * 100;

  // ── Loading state ─────────────────────────────────────────────────────────
  if (authStatus === "loading") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          bgcolor: "#f8f9fc",
        }}
      >
        <CircularProgress size={32} sx={{ color: "#0d9488" }} />
      </Box>
    );
  }

  return (
    /*
     * Layout strategy - desktop vs mobile:
     *
     * Desktop (md+):
     *   The app panel is `position: fixed` - pinned to the right 55% of the
     *   viewport at all times. `position: sticky` inside flex has too many
     *   browser-specific pitfalls (flex stretch, overflow ancestors, etc.).
     *   Fixed is unambiguous: it's always in the viewport.
     *   The narrative column gets `marginRight: "55%"` to live in the left 45%.
     *
     * Mobile (xs):
     *   Panel is `position: relative`, full-width, 58 vh tall - sits at the
     *   top of the page. Cards scroll below it in normal document flow.
     */
    <Box sx={{ bgcolor: "#f8f9fc", height: { xs: "100dvh", md: "100vh" }, overflow: "hidden" }}>

      {/* ── Progress bar ───────────────────────────────────────────────────── */}
      <Box
        role="progressbar"
        aria-valuenow={Math.round(progress)}
        sx={{
          position: "fixed",
          top: 0, left: 0,
          width: "100%", height: "3px",
          // Opaque equivalent of rgba(13,148,136,0.15) over the #f8f9fc page bg —
          // same look, but stops page text bleeding through the track while scrolling.
          bgcolor: "#d5eaeb",
          zIndex: 1200,
        }}
      >
        <Box sx={{ height: "100%", width: `${progress}%`, bgcolor: "#0d9488", transition: "width 0.4s ease" }} />
      </Box>

      {/* ── Branding strip ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          position: "fixed",
          top: 3, left: 0, right: 0,
          height: 44,
          display: "flex", alignItems: "center",
          px: 3,
          zIndex: 1100,
          bgcolor: "rgba(248,249,252,0.92)",
          backdropFilter: "blur(8px)",
          borderBottom: "0.5px solid rgba(15,23,42,0.07)",
        }}
      >
        <Typography sx={{ fontSize: "13px", fontWeight: 700, fontFamily: "'DM Sans', sans-serif", color: "#0f172a", letterSpacing: "-0.01em" }}>
          Constituency Profiles
        </Typography>
        <Box sx={{ ml: 1.5, px: 1, py: 0.25, borderRadius: "5px", bgcolor: "rgba(13,148,136,0.09)", border: "1px solid rgba(13,148,136,0.2)" }}>
          <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#0d9488", fontFamily: "'DM Sans', sans-serif" }}>
            Tax Case Study
          </Typography>
        </Box>
        {/* X close button - always visible, returns to main app */}
        <Box
          onClick={() => navigate(shouldGoMobile() ? "/m?section=map" : "/explore")}
          sx={{
            ml: "auto",
            width: 28, height: 28,
            display: "flex", alignItems: "center", justifyContent: "center",
            borderRadius: "6px",
            cursor: "pointer",
            color: "rgba(15,23,42,0.4)",
            "&:hover": { bgcolor: "rgba(15,23,42,0.06)", color: "rgba(15,23,42,0.7)" },
            transition: "all 0.15s ease",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </Box>
      </Box>

      {/* ── App panel ──────────────────────────────────────────────────────── */}
      {/* Desktop: fixed to right 55% of viewport, below the 47px branding strip */}
      {/* Mobile: fixed, full width, 30dvh — sits above the narrative cards  */}
      <Box
        ref={(el) => { appPanelRef.current = el; rightPanelRef.current = el; }}
        sx={{
          position: "fixed",
          top: { xs: mapFullscreen ? 0 : "47px", md: "47px" },
          left: { xs: 0, md: "auto" },
          right: 0,
          width: { xs: "100%", md: isFinalStep ? "100%" : "45%", lg: isFinalStep ? "100%" : "48%", xl: isFinalStep ? "100%" : "50%" },
          height: { xs: mapFullscreen ? "100dvh" : (isFinalStep ? 0 : "30dvh"), md: "calc(100vh - 47px)" },
          zIndex: { xs: mapFullscreen ? 1300 : 5, md: 5 },
          overflow: "hidden",
          borderLeft: { sm: isFinalStep ? "none" : "0.5px solid rgba(15,23,42,0.07)" },
          bgcolor: "background.paper",
          transition: "width 0.45s ease, height 0.3s ease, top 0.3s ease",
        }}
      >
        <Box sx={{ height: "100%", position: "relative", overflow: "hidden" }}>
          {(() => {
            const isReportsStep = STEPS[activeIndex]?.name === "reports";
            const blurMap = isFinalStep || isReportsStep;
            return (
              <>
                <Box sx={{ height: "100%", filter: blurMap ? "blur(10px) saturate(0.85)" : "none", transform: blurMap ? "scale(1.03)" : "none", transition: "filter 0.4s ease, transform 0.4s ease" }}>
                  <AppPanelWrapper ref={mainRef} />
                </Box>

                {/* Mobile touch blocker — only allow map interaction when fullscreen */}
                <Box sx={{
                  display: { xs: mapFullscreen ? "none" : "block", sm: "none" },
                  position: "absolute", inset: 0, zIndex: 8,
                }} onClick={() => setMapFullscreen(true)} />

                {(isFinalStep) && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      bgcolor: "rgba(248,249,252,0.3)",
                      backgroundImage: "linear-gradient(180deg, rgba(248,249,252,0.12) 0%, rgba(248,249,252,0.5) 100%)",
                      pointerEvents: "none",
                      zIndex: 2,
                    }}
                  />
                )}

                {isReportsStep && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      p: { xs: 2, md: 4 },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: 340,
                        borderRadius: "14px",
                        overflow: "hidden",
                        boxShadow: "0 24px 64px rgba(0,0,0,0.28), 0 4px 16px rgba(0,0,0,0.12)",
                        animation: "reportIn 0.5s cubic-bezier(0.22,1,0.36,1) both",
                        "@keyframes reportIn": {
                          from: { opacity: 0, transform: "translateY(18px) scale(0.97)" },
                          to:   { opacity: 1, transform: "translateY(0) scale(1)" },
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src="/static/images/report_ex.png"
                        alt="Example constituency report — North Norfolk"
                        sx={{ display: "block", width: "100%", height: "auto" }}
                      />
                    </Box>
                  </Box>
                )}
              </>
            );
          })()}

          {/* Map label - floats top-left, shows what variable is being displayed */}
          <Box
            sx={{
              position: "absolute",
              top: 12, left: 12,
              zIndex: 10,
              pointerEvents: "none",
              opacity: (isFinalStep || STEPS[activeIndex]?.name === "reports") ? 0 : 1,
              transition: "opacity 0.25s ease",
            }}
          >
            <Box
              key={activeIndex}
              sx={{
                display: "inline-flex", alignItems: "center", gap: 0.75,
                px: 1.5, py: 0.65,
                borderRadius: "8px",
                bgcolor: "rgba(255,255,255,0.93)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(13,148,136,0.18)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#0d9488", flexShrink: 0 }} />
              <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#0f172a", fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.01em", lineHeight: 1 }}>
                {STEPS[activeIndex]?.mapLabel}
              </Typography>
            </Box>
          </Box>

          {/* Hover hint — tourist-tax-support and local-messaging steps */}
          {(STEPS[activeIndex]?.name === "tourist-tax-support" || STEPS[activeIndex]?.name === "local-messaging") && (
            <Box sx={{
              position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)",
              zIndex: 12, display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 0.8,
              px: 1.5, py: 0.75, borderRadius: "999px",
              bgcolor: "rgba(15,23,42,0.72)", backdropFilter: "blur(8px)",
              boxShadow: "0 4px 18px rgba(0,0,0,0.18)",
              whiteSpace: "nowrap", pointerEvents: "none",
              animation: "fuIn 0.5s cubic-bezier(0.22,1,0.36,1) 0.8s both", ...FU_KF,
            }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
              </svg>
              <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.88)", fontFamily: F, fontWeight: 600, letterSpacing: "0.01em", lineHeight: 1 }}>
                Hover over a constituency to explore
              </Typography>
            </Box>
          )}

          {STEPS[activeIndex]?.name === "mp-targeting" && (
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                position: "absolute",
                top: 12, right: 12,
                zIndex: 10,
                pointerEvents: "none",
                bgcolor: "rgba(255,255,255,0.93)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(13,148,136,0.18)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                borderRadius: "10px",
                px: 2, py: 1.75,
                width: 380,
              }}
            >
              <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#374151", fontFamily: F, mb: 1.5 }}>
                Labour 2024 voters
              </Typography>
              {IMPACT_CLUSTERS.map((c) => (
                <Box key={c.label} sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.1 }}>
                  <Typography sx={{ fontSize: "11px", color: "#64748b", fontFamily: F, width: 155, flexShrink: 0, textAlign: "right", lineHeight: 1.3 }}>{c.label}</Typography>
                  <Box sx={{ flex: 1, height: 26, borderRadius: "4px", bgcolor: `${c.color}18`, position: "relative" }}>
                    <Box sx={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${c.lab}%`, bgcolor: c.color, borderRadius: "4px" }} />
                  </Box>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#0f172a", fontFamily: F, width: 34, textAlign: "right" }}>{c.lab}%</Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* X button — zoom out from MP seat */}
          {STEPS[activeIndex]?.name === "mp-targeting" && zoomedMpCode && (
            <Box
              onClick={() => {
                setZoomedMpCode(null);
                if (mainRef?.current) {
                  mainRef.current.setState({ chatHighlightAreas: [], chatShouldZoom: false });
                  mainRef.current.flyTo(UK_FIT(1000, { top: 20, bottom: 20, left: 20, right: 120 }));
                }
              }}
              sx={{
                position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)",
                zIndex: 12, display: "flex", alignItems: "center", gap: 0.75,
                px: 1.5, py: 0.75, borderRadius: "999px",
                bgcolor: "rgba(15,23,42,0.72)", backdropFilter: "blur(8px)",
                boxShadow: "0 4px 18px rgba(0,0,0,0.18)",
                cursor: "pointer", whiteSpace: "nowrap",
                animation: "fuIn 0.35s cubic-bezier(0.22,1,0.36,1) both", ...FU_KF,
                "&:hover": { bgcolor: "rgba(15,23,42,0.85)" },
              }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.88)" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.88)", fontFamily: F, fontWeight: 600, letterSpacing: "0.01em", lineHeight: 1 }}>
                Back to full map
              </Typography>
            </Box>
          )}

          {/* Wave toggle - step 2 (back-to-basics) and step 4 (energy-trust) */}
          {(STEPS[activeIndex]?.name === "intro" || STEPS[activeIndex]?.name === "back-to-basics" || STEPS[activeIndex]?.name === "energy-trust") && (
            <Box
              sx={{
                position: "absolute",
                top: 12, right: 12,
                zIndex: 10,
                display: "flex",
                borderRadius: "10px",
                overflow: "hidden",
                border: "1px solid rgba(13,148,136,0.25)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              }}
            >
              {[3, 4].map((w) => {
                const active = (waveOverride ?? 4) === w;
                return (
                  <Box
                    key={w}
                    onClick={() => handleWaveToggle(w)}
                    sx={{
                      px: 1.75, py: 0.75,
                      cursor: "pointer",
                      bgcolor: active ? "#0d9488" : "rgba(255,255,255,0.93)",
                      backdropFilter: "blur(10px)",
                      color: active ? "#fff" : "#475569",
                      fontSize: "11px",
                      fontWeight: 700,
                      fontFamily: "'DM Sans', sans-serif",
                      letterSpacing: "0.03em",
                      transition: "all 0.18s ease",
                      userSelect: "none",
                      "&:hover": { bgcolor: active ? "#0f766e" : "rgba(13,148,136,0.08)" },
                    }}
                  >
                    {w === 3 ? "Q1 2026" : "Q2 2026"}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* ── Map fullscreen toggle — xs only ──────────────────────────── */}
          <Box
            onClick={() => setMapFullscreen(f => !f)}
            sx={{
              display: { xs: "flex", md: "none" },
              position: "absolute",
              bottom: 12, right: 12,
              zIndex: 20,
              width: 36, height: 36,
              borderRadius: "10px",
              bgcolor: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(13,148,136,0.2)",
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              alignItems: "center", justifyContent: "center",
              cursor: "pointer",
              userSelect: "none",
              WebkitTapHighlightColor: "transparent",
              transition: "background 0.15s ease",
              "&:active": { bgcolor: "rgba(13,148,136,0.12)" },
            }}
          >
            {mapFullscreen ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
                <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
              </svg>
            )}
          </Box>

        </Box>
      </Box>


      {/* ── Left dot nav - fixed, desktop only ──────────────────────────────── */}
      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          position: "fixed",
          left: 14,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1050,
          opacity: isFinalStep ? 0 : 1,
          transition: "opacity 0.4s ease",
          pointerEvents: isFinalStep ? "none" : "auto",
        }}
      >
        {STEPS.filter((s) => !s.isCta).map((_, i) => (
          <Box
            key={i}
            onClick={() => scrollToIndex(i)}
            title={STEPS[i]?.headline?.slice(0, 48)}
            sx={{
              width: activeIndex === i ? 8 : 5,
              height: activeIndex === i ? 8 : 5,
              borderRadius: "50%",
              bgcolor: activeIndex === i ? TEAL : `${TEAL}30`,
              transition: "all 0.25s ease",
              flexShrink: 0,
              cursor: "pointer",
              "&:hover": { bgcolor: TEAL, transform: "scale(1.35)" },
            }}
          />
        ))}
        <Box sx={{ mt: 0.5, animation: "nudgeDown 1.8s ease-in-out infinite", "@keyframes nudgeDown": { "0%,100%": { transform: "translateY(0)", opacity: 0.5 }, "50%": { transform: "translateY(4px)", opacity: 1 } } }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </Box>
        <Typography sx={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: TEAL, fontFamily: F, writingMode: "vertical-rl", mt: 0.5 }}>scroll</Typography>
      </Box>

      {/* ── Scroll bumper — gradient fade at bottom of left panel, slide 0 only  */}
      <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "center", gap: 0.5, position: "fixed", bottom: 0, left: 0, width: { sm: "50%", md: "50%", lg: "50%", xl: "50%" }, pb: 2, pt: 5, background: "linear-gradient(to bottom, transparent, rgba(250,250,252,0.96) 55%)", zIndex: 1040, pointerEvents: "none", opacity: activeIndex === 0 ? 1 : 0, transition: "opacity 0.5s ease" }}>
        <Box sx={{ animation: "nudgeDown2 1.6s ease-in-out infinite", "@keyframes nudgeDown2": { "0%,100%": { transform: "translateY(0)", opacity: 0.5 }, "50%": { transform: "translateY(5px)", opacity: 1 } } }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </Box>
        <Typography sx={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: TEAL, fontFamily: F, opacity: 0.7 }}>Scroll</Typography>
      </Box>

      {/* Mobile bottom nav bar - xs only */}
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          position: "fixed",
          bottom: 0, left: 0, right: 0,
          height: 54,
          bgcolor: "rgba(248,249,252,0.96)",
          backdropFilter: "blur(12px)",
          borderTop: "0.5px solid rgba(15,23,42,0.07)",
          zIndex: 1050,
          alignItems: "center",
          px: 1.5,
          gap: 1,
        }}
      >
        <Box
          onClick={() => { const prev = Math.max(0, activeIndex - 1); setActiveIndex(prev); }}
          sx={{ width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "10px", bgcolor: activeIndex === 0 ? "transparent" : "rgba(13,148,136,0.08)", cursor: activeIndex === 0 ? "default" : "pointer", flexShrink: 0 }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={activeIndex === 0 ? MUTED_LIGHT : TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="18 15 12 9 6 15"/>
          </svg>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, px: 0.5 }}>
          <Typography sx={{ fontSize: "10px", fontWeight: 700, color: TEAL, fontFamily: F, letterSpacing: "0.06em", textTransform: "uppercase", lineHeight: 1.2 }}>
            {activeIndex + 1} of {STEPS.length}
          </Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 500, color: INK, fontFamily: F, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {STEPS[activeIndex]?.headline}
          </Typography>
        </Box>
        <Box
          onClick={() => { const next = Math.min(STEPS.length - 1, activeIndex + 1); setActiveIndex(next); }}
          sx={{ width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "10px", bgcolor: activeIndex === STEPS.length - 1 ? "transparent" : "rgba(13,148,136,0.08)", cursor: activeIndex === STEPS.length - 1 ? "default" : "pointer", flexShrink: 0 }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={activeIndex === STEPS.length - 1 ? MUTED_LIGHT : TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </Box>
      </Box>

      {/* ── Narrative cards column - snap scroll container ─────────────────── */}
      <Box
        ref={scrollContainerRef}
        onTouchStart={(e) => {
          scrollContainerRef.current._touchY = e.touches[0].clientY;
          scrollContainerRef.current._touchT = Date.now();
        }}
        onTouchEnd={(e) => {
          const startY = scrollContainerRef.current._touchY;
          const startT = scrollContainerRef.current._touchT;
          if (startY == null) return;
          const delta = startY - e.changedTouches[0].clientY;
          const elapsed = Date.now() - (startT || 0);
          // Require a deliberate swipe: at least 80px and within 600ms
          if (Math.abs(delta) < 80 || elapsed > 600) return;
          const isMobile = window.innerWidth < 900;
          if (!isMobile) return;
          if (scrollContainerRef.current._swiping) return;
          scrollContainerRef.current._swiping = true;
          setTimeout(() => { scrollContainerRef.current._swiping = false; }, 800);
          const next = delta > 0
            ? Math.min(activeIndex + 1, STEPS.length - 1)
            : Math.max(activeIndex - 1, 0);
          if (next !== activeIndex) setActiveIndex(next);
        }}
        sx={{
          mr: { xs: 0, md: isFinalStep ? 0 : "45%", lg: isFinalStep ? 0 : "48%", xl: isFinalStep ? 0 : "50%" },
          /* mobile: fixed below map, fills remaining space above nav bar */
          pt: { xs: 0, md: "47px" },
          mt: { xs: isFinalStep ? "47px" : "calc(30dvh + 47px)", md: 0 },
          height: { xs: isFinalStep ? "calc(100dvh - 47px - 54px)" : "calc(70dvh - 47px - 54px)", md: "100vh" },
          overflowX: "hidden",
          overflowY: { xs: "hidden", md: "scroll" },
          scrollSnapType: { xs: "none", md: "y mandatory" },
          // Hide scrollbar visually but keep it functional
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {STEPS.map((step, i) => (
          <NarrativeCard
            key={step.name}
            step={step}
            index={i}
            total={STEPS.length}
            isActive={activeIndex === i}
            authState={authState}
            cardRef={(el) => { cardRefs.current[i] = el; }}
            onScrollNext={() => scrollToIndex(i + 1)}
            mainRef={mainRef}
            zoomedMpCode={zoomedMpCode}
            onMpZoom={setZoomedMpCode}
          />
        ))}
      </Box>
      <ScrollStartHint visible={authStatus === "ready" && activeIndex === 0 && showStartHint} />
    </Box>
  );
}
